/**
 * Module dependencies.
 */

import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from 'app/components/organisms/crud/form-types';

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { pick, omit } from 'lodash';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import moment from 'moment';
import { TFunction } from 'react-i18next';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { formFields } from './form-fields';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import { Subscription } from 'app/types/subscription';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<any> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    key: 'id',
    size: '100px',
    render: item => <IdCopy>{item.id}</IdCopy>
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    supportLocale: true,
    key: 'name',
    size: '1fr',
    render: item => item.name
  },
  {
    title: translate('common.table.columns.pharmaciesNumber'),
    dataIndex: 'pharmaciesNumber',
    key: 'pharmaciesNumber',
    size: '150px',
    render: item => item.pharmacies.length
  },
  {
    title: translate('common.table.columns.usersNumber'),
    dataIndex: 'usersNumber',
    key: 'usersNumber',
    size: '150px',
    render: item => item.users.length
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '200px',
    render: item => <DateTime date={item.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    size: '200px',
    render: item => <DateTime date={item.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  }
];

/**
 * Remove.
 */

const remove = (): CrudRemoveType<Subscription> => ({
  endpoint: apiEndpoints.subscriptionsById
});

/**
 * Normalize payload.
 */

function normalizePayload(values: any) {
  const result = omit(values, ['id', 'createdAt', 'updatedAt']);

  return result;
}

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType<Subscription> => ({
  formFields: formFields(translate, 'edit'),
  languages: '*',
  endpoint: apiEndpoints.subscriptionsById,
  redirect: appRoutes.dashboard.services.subscriptionsList,
  resolveParams: (params: any) => params,
  route: appRoutes.dashboard.services.subscriptionsEdit,
  submitLabel: translate('common.actions.edit'),
  normalizePayload,
  normalizeInitialValues: (keys: string[], payload: Subscription) => {
    const data: any = pick(payload, keys);

    data.createdAt = moment(data.createdAt).format('YYYY-MM-DD HH:mm:ss');
    data.updatedAt = moment(data.updatedAt).format('YYYY-MM-DD HH:mm:ss');

    return data;
  }
});

/**
 * List.
 */

const list = (translate: TFunction): CrudListType<Subscription> => ({
  columns: listColumns(translate),
  filters: listFilters(translate),
  enableExport: true,
  resolveParams: (params: any, item?: Subscription) => {
    return {
      id: params?.id ?? item?.id
    };
  },
  route: appRoutes.dashboard.services.subscriptionsList,
  addButton: translate('common.actions.add'),
  clickLine: (item, { navigate }) => {
    const route = resolveAppPath(appRoutes.dashboard.services.subscriptionsDetail.users, {
      subscriptionId: item.id
    });

    navigate(route);
  },
  canEdit: true,
  canRemove: true,
  key: ['subscriptions'],
  normalizeData: (result: any) => {
    return result;
  },
  pagination: {
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100, 500, 1000]
  },
  endpoint: apiEndpoints.subscriptions,
  endpointById: apiEndpoints.subscriptionsById
});

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<Subscription> => ({
  formFields: formFields(translate, 'add'),
  normalizePayload
});

/**
 * Config.
 */

export function createSubscriptionsTemplate(translate: TFunction): CrudTemplate<Subscription> {
  return normalizeTemplate(translate, {
    add: add(translate),
    edit: edit(translate),
    list: list(translate),
    remove: remove()
  });
}
